import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useCallback, useMemo, useEffect } from 'react';
import MultiSelectAutocompleteFilter from '../../../../Filters/MultiSelectAutocompleteFilter';
import Dropdown from '../../../../Dropdown';
import Filter from '../../../../Filters/Filter';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import { autocompleteRequest } from '../../../../../store/slices/autocomplete';
import {
  AutocompleteType, FlightSearchTypes, airlinesOptions, onlyAirlineOption,
} from '../../../../../constants';

import Checkbox from '../../../../Checkbox';
import DropdownSelectFilter from '../../../../Filters/DropdownSelectFilter';

const AirlinesLookup = ({
  tripId, filterBgColor,
}) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;

  const { airlinesAutocompleteResults } = useSelector((store) => store.autocomplete);
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const isEmptyFilter = useMemo(() => !_.find(searchInputs.flightSegments,
    (fs) => fs.airlines && fs.airlines.length > 0), [searchInputs.flightSegments]);

  const handleChange = (newValue) => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };

    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
      payload.searchObj.flightSegments[0] = {
        ...payload.searchObj.flightSegments[0],
        ..._.omit(newValue, 'rowIndex'),
      };
    } else if (searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY
      || searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP) {
      payload.searchObj.flightSegments[newValue.rowIndex] = {
        ...payload.searchObj.flightSegments[newValue.rowIndex],
        ..._.omit(newValue, 'rowIndex'),
      };
    }

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('AirlinesLookup.handleChange', {
      ...newValue,
    });
  };

  const handleHasDifferentAirline = useCallback((newValue) => {
    const payload = {
      tripId,
      searchObj: {
        ...newValue,
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };
    payload.searchObj.flightSegments.forEach((segment, index) => {
      payload.searchObj.flightSegments[index] = {
        ...payload.searchObj.flightSegments[index],
        airlines: '',
      };
    });
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('AirlinesLookup.handleHasDifferentAirline', {
      ...newValue,
    });
  }, [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId]);

  const handleClearFilter = useCallback(() => {
    handleHasDifferentAirline({
      hasDifferentOutboundInboundAirLine: false,
    });

    track('filterBar.clearFilter');
  }, [handleHasDifferentAirline, track]);

  useEffect(() => {
    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY
      && searchInputs.hasDifferentOutboundInboundAirLine) {
      handleHasDifferentAirline({
        hasDifferentOutboundInboundAirLine: false,
      });
    }
  }, [searchInputs, handleHasDifferentAirline]);

  const handleInputChange = useCallback((query) => {
    if (query && query.length < 2) return;
    dispatch(autocompleteRequest({
      autocompleteTypes: AutocompleteType.AIRLINES,
      responseType: 'airlinesAutocompleteResults',
      query,
    }));
  }, [dispatch]);

  const options = searchInputs && searchInputs.hasDifferentOutboundInboundAirLine
    ? onlyAirlineOption : airlinesOptions;

  const airlineInputs = (airlinePreferenceProperty, airlines, flightSegmetRowIndex = 0) => {
    const airlinePreference = searchInputs && searchInputs.flightSegments
    && searchInputs.flightSegments[flightSegmetRowIndex][airlinePreferenceProperty];
    const matchingOption = options.find((o) => o.value === airlinePreference);
    const labels = matchingOption ? matchingOption.label : options[0].label;

    return (
      <div className="flex font-normal">
        <div>
          <DropdownSelectFilter
            onChange={(newValue) => handleChange(
              { [airlinePreferenceProperty]: newValue, rowIndex: flightSegmetRowIndex },
            )}
            label={labels}
            options={options}
            selectedOptionValue={(searchInputs.flightSegments
            && searchInputs.flightSegments[flightSegmetRowIndex]
            && searchInputs.flightSegments[flightSegmetRowIndex][airlinePreferenceProperty])
              ? searchInputs.flightSegments[flightSegmetRowIndex][airlinePreferenceProperty]
              : options[0].value}
            defaultOptionValue={(searchInputs.flightSegments
                && searchInputs.flightSegments[flightSegmetRowIndex]
                && searchInputs.flightSegments[flightSegmetRowIndex][airlinePreferenceProperty])
                || options[0].value}
            menuWidth="w-64"
            filterButtonMaxWidth="max-w-full w-24"
            filterSpacingClass="pl-2 py-2 pr-2"
            variant="outline-left-radius"
            fontSize="font-normal"
            disabled={searchInputs.hasDifferentOutboundInboundAirLine && true}
          />
        </div>
        <MultiSelectAutocompleteFilter
          onChange={(newValue) => handleChange({
            [airlines]: newValue, rowIndex: flightSegmetRowIndex,
          })}
          label="Airlines Preference"
          tooltipPrependText="Airlines Preference"
          options={airlinesAutocompleteResults}
          filterMaxWidthClass="max-w-full w-48"
          onInputChange={handleInputChange}
          filterSpacingClass="px-4 py-2"
          values={(searchInputs && searchInputs.flightSegments
            && searchInputs.flightSegments[flightSegmetRowIndex]
            && searchInputs.flightSegments[flightSegmetRowIndex].airlines)
           || searchInputs.airlines}
          bgClassName={!isTripSearch ? '' : filterBgColor}
          variant="outline-right-radius"
        />
      </div>
    );
  };

  return (
    <Filter
      filterMaxWidthClass="max-w-full"
      label="Airlines Preference"
      menuWidthClass="w-max"
      isFilterEmpty={isEmptyFilter}
      handleClear={handleClearFilter}
      variant={isTripSearch ? undefined : 'outline'}
      bgClassName={!isTripSearch ? '' : filterBgColor}
    >
      <Dropdown.Item withGridLayout={false}>
        {searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY ? (
          <Checkbox
            checked={!!searchInputs.hasDifferentOutboundInboundAirLine}
            onChange={(event, newValue) => handleHasDifferentAirline({
              hasDifferentOutboundInboundAirLine: newValue,
            })}
            label={searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP ? 'Different outbound/inbound airlines' : 'Different airlines for all flights'}
          />
        ) : <></>}
        {searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY && airlineInputs('airlinesPreference', 'airlines')}
        {!searchInputs.hasDifferentOutboundInboundAirLine
        && searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY && airlineInputs('airlinesPreference', 'airlines')}

        {searchInputs.hasDifferentOutboundInboundAirLine
        && searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
          ? (
            <>
              <div className="mt-2 font-bold">Outbound:</div>
              {airlineInputs('airlinesPreference', 'airlines', 0)}
              <div className="mt-5 font-bold">Inbound:</div>
              {airlineInputs('airlinesPreference', 'airlines', 1)}
            </>
          ) : <></>}

        {searchInputs.hasDifferentOutboundInboundAirLine
         && searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY
        && searchInputs.flightSegments.map((flightSegment, currentIndex) => (
          <>
            <div key={`${currentIndex.toString()}`} className="mt-4 font-bold">
              Flight
              <span className="mx-1">
                {currentIndex + 1}
                :
              </span>
            </div>
            {airlineInputs('airlinesPreference', 'airlines', currentIndex)}
          </>
        ))}

      </Dropdown.Item>
    </Filter>
  );
};

AirlinesLookup.propTypes = {
  tripId: PropTypes.string,
  filterBgColor: PropTypes.string,
};

AirlinesLookup.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
};

export default AirlinesLookup;
